import { BUILDINGS, MAX_HUBS_PER_PLAYER } from '../data/buildings.js';
import { getMap } from '../data/maps/index.js';
import type { MatchConfig } from '../data/matchConfig.js';
import { MATCH_CONFIG_V0_4 } from '../data/matchConfig.js';
import { SQUAD_DEFS } from '../data/squads.js';
import { aStar } from '../pathfinding/AStar.js';
import { buildGrid, cellCenter, isBlocked, worldToCell } from '../pathfinding/Grid.js';
import type { PathGrid } from '../pathfinding/Grid.js';
import { unreachable } from '../utils/assert.js';

import type { MatchState } from './State.js';
import { SECONDS_PER_TICK, createInitialState } from './State.js';
import { isSlotOccupied, slotWorldPos } from './buildSlots.js';
import type { Command } from './commands/Command.js';
import type { Building, PermanentBuildingKind } from './entities/Building.js';
import type { ProductionSlot } from './entities/Core.js';
import type { Squad } from './entities/Squad.js';
import type { EntityId, PlayerId } from './ids.js';
import { entityId, tickOf } from './ids.js';
import { abilitySystem, applyAbilityCommand } from './systems/AbilitySystem.js';
import { buildingSystem } from './systems/BuildingSystem.js';
import { campSystem } from './systems/CampSystem.js';
import { captureSystem } from './systems/CaptureSystem.js';
import { combatSystem } from './systems/CombatSystem.js';
import { doctrineSystem } from './systems/DoctrineSystem.js';
import { economySystem } from './systems/EconomySystem.js';
import { fogSystem } from './systems/FogSystem.js';
import { tickForwardTurretNetwork } from './systems/ForwardTurretNetworkSystem.js';
import { movementSystem } from './systems/MovementSystem.js';
import { productionSystem } from './systems/ProductionSystem.js';
import { signalLinkSystem } from './systems/SignalLinkSystem.js';
import { statsSystem } from './systems/StatsSystem.js';
import { winConditionSystem } from './systems/WinConditionSystem.js';

const PRODUCE_QUEUE_LIMIT = 4; // bounded by squadCap; production never exceeds the cap.
const GROUP_FORMATION_SPACING_PX = 56;
// Stand-off radii applied to move/attack-move order targets: if a player clicks
// inside a structure footprint the order target is pushed outward to the edge
// of the standoff ring. Prevents squads from visually piling on top of a Core
// or Hub when the player clicked on the structure to give a "go there" order.
// Standoff is per-structure-kind (Cores are bigger than Hubs).
const CORE_STANDOFF_PX = 100;
const BUILDING_STANDOFF_PX = 60;

// Pushes `target` to the perimeter of the nearest Core or Building whose
// footprint contains it. Returns the input position unmodified if no structure
// is overlapping. Pure — operates on copies so callers can keep their
// originals.
function clampTargetToStructureStandoff(state: MatchState, target: { x: number; y: number }): { x: number; y: number } {
  for (const core of Object.values(state.cores)) {
    const dx = target.x - core.pos.x;
    const dy = target.y - core.pos.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < CORE_STANDOFF_PX * CORE_STANDOFF_PX) {
      const dist = Math.sqrt(distSq);
      if (dist < 0.001) {
        // Click exactly on Core center — pick an arbitrary outward direction.
        return { x: core.pos.x + CORE_STANDOFF_PX, y: core.pos.y };
      }
      const inv = CORE_STANDOFF_PX / dist;
      return { x: core.pos.x + dx * inv, y: core.pos.y + dy * inv };
    }
  }
  for (const b of Object.values(state.buildings)) {
    // mini_turret is a deployed entity that's expected to be walked over —
    // skip it so squads can still pass through their own Engineer's turret.
    if (b.kind === 'mini_turret') continue;
    const dx = target.x - b.pos.x;
    const dy = target.y - b.pos.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < BUILDING_STANDOFF_PX * BUILDING_STANDOFF_PX) {
      const dist = Math.sqrt(distSq);
      if (dist < 0.001) {
        return { x: b.pos.x + BUILDING_STANDOFF_PX, y: b.pos.y };
      }
      const inv = BUILDING_STANDOFF_PX / dist;
      return { x: b.pos.x + dx * inv, y: b.pos.y + dy * inv };
    }
  }
  return target;
}

// The simulation owns mutation. Nothing else writes to MatchState.
// Tick order: commands → buildings → production → movement → combat → capture → economy → ability → fog → doctrine → stats → win-check → time.
// BuildingSystem runs before ProductionSystem so a Hub that finished construction
// last tick is operational and its parallel queue advances this tick alongside
// the Core's. ProductionSystem still runs before movement/combat so a freshly
// spawned squad participates in the same tick.
// FogSystem runs after capture so newly-flipped relays' vision is applied this
// tick, and after combat so squads that died this tick no longer contribute
// vision (matches "no ghost units" in docs/02_GAME_DESIGN.md §5).
// AbilitySystem runs after combat so cooldowns tick down only after the system
// that observes them this tick has finished, and before fog/win-check so cleanup
// of expired marks/bubbles/mini-turrets is reflected in the broadcast snapshot.
// DoctrineSystem (V1.5) runs after the gameplay systems and before stats/win-check
// so a fresh window-open boundary lands in the same broadcast as the tick that
// reached 3:00, and so a window-close auto-assigns vanguard before any UI reads.
// Win-check runs last so it observes the final state of the tick before
// matchSeconds advances; this keeps the outcome's recorded tick consistent with
// what subscribers see.
export class Simulation {
  state: MatchState;
  // Pathfinding grid is derived from static map data; not part of MatchState (it
  // doesn't need to round-trip), and is identical for any state created from the
  // same map, so determinism is preserved.
  readonly grid: PathGrid;

  constructor(seed: number, config: MatchConfig = MATCH_CONFIG_V0_4) {
    this.state = createInitialState(seed, config);
    // Pathfinding grid is derived from the chosen map (defaults to Foundry).
    // Keeping it off MatchState matches V0.x behavior — the grid is identical
    // for any state created from the same map id, so determinism holds.
    this.grid = buildGrid(getMap(this.state.mapId));
  }

  tick(commands: readonly Command[]): void {
    // Once the match has ended, freeze the simulation: drop incoming commands and stop
    // advancing time/systems. Subscribers keep observing the terminal snapshot.
    if (this.state.outcome !== null) return;
    for (const cmd of commands) {
      this.applyCommand(cmd);
    }
    buildingSystem(this.state);
    productionSystem(this.state);
    movementSystem(this.state, this.grid);
    // CampSystem runs after movement so player squads' new positions are
    // visible for aggro reads, and BEFORE combat so camp units that spawn
    // this tick participate in the same tick's gunplay. Cache pickup also
    // runs here so a squad that ends its movement step inside a cache picks
    // up the same tick.
    campSystem(this.state);
    combatSystem(this.state);
    captureSystem(this.state);
    // SignalLinkSystem runs AFTER capture (so freshly flipped relays read the
    // current owner) and BEFORE economy / fog (so they consume the up-to-date
    // isConnected flag this same tick).
    signalLinkSystem(this.state);
    // V1.5+ forward turret network dependency. Must run AFTER signalLinkSystem
    // (reads node.isConnected) and BEFORE fogSystem (reads
    // building.isOnline). CombatSystem ran earlier in the tick — its
    // isOnline read sees the previous tick's value, a deliberate ≤1-tick lag
    // (50 ms) from a relay flip to the turret stopping fire. Same-tick
    // gating would require also moving combat after capture, which would
    // change "dead-this-tick squads can't capture" semantics that other
    // systems rely on.
    tickForwardTurretNetwork(this.state);
    economySystem(this.state);
    abilitySystem(this.state);
    fogSystem(this.state);
    doctrineSystem(this.state);
    // statsSystem runs after capture/economy so a sample reflects post-tick
    // ownership and income, and before win-check so the closing sample is
    // captured even on the tick that ends the match.
    statsSystem(this.state);
    winConditionSystem(this.state);
    this.state.tick = tickOf(this.state.tick + 1);
    this.state.matchSeconds += SECONDS_PER_TICK;
  }

  // Validation: drop commands targeting entities the issuer does not own. We never throw
  // on bad input — this layer is reached by faked clients in V2 and must be defensive.
  private applyCommand(cmd: Command): void {
    switch (cmd.kind) {
      case 'move': {
        const target = clampTargetToStructureStandoff(this.state, { x: cmd.target.x, y: cmd.target.y });
        const assignments = groupMoveAssignments(this.state, this.grid, cmd.squadIds, cmd.issuedBy, target);
        for (const { squad, target: squadTarget } of assignments) {
          // Replan: replace any current order. Computing the path eagerly here means
          // MovementSystem can stay O(N) over squads without re-running A*.
          squad.orderQueue = [{ kind: 'move', target: squadTarget }];
          squad.path = aStar(this.grid, squad.pos, squadTarget);
          squad.pathIndex = 0;
          // V1.5+ Slice 2 — issuing any movement order cancels an active
          // channel. The player chose to relocate over completing the cast.
          squad.channelingAbility = null;
        }
        return;
      }
      case 'attack_move': {
        const target = clampTargetToStructureStandoff(this.state, { x: cmd.target.x, y: cmd.target.y });
        const assignments = groupMoveAssignments(this.state, this.grid, cmd.squadIds, cmd.issuedBy, target);
        for (const { squad, target: squadTarget } of assignments) {
          squad.orderQueue = [{ kind: 'attack_move', target: squadTarget }];
          squad.path = aStar(this.grid, squad.pos, squadTarget);
          squad.pathIndex = 0;
          squad.channelingAbility = null;
        }
        return;
      }
      case 'produce_squad': {
        // producerId can target a Core (V0.3 path, before any Hub) or an
        // operational Hub (V0.4 path, once one exists). Both share ProductionSlot.
        const slot = resolveProducer(this.state, cmd.producerId, cmd.issuedBy);
        if (slot === null) return;
        if (slot.queue.length >= PRODUCE_QUEUE_LIMIT) return;
        const player = this.state.players[cmd.issuedBy];
        const def = SQUAD_DEFS[cmd.archetype];
        if (player.signal < def.cost) return;
        if (player.squadCount + slot.queue.length >= player.squadCap) return;
        player.signal -= def.cost;
        slot.queue.push(cmd.archetype);
        return;
      }
      case 'build': {
        // BuildCommand is restricted to player-buildable kinds. mini_turret spawns
        // from Engineer's Q ability and is intentionally rejected here.
        if (!isPermanentBuildingKind(cmd.building)) return;
        const core = this.state.cores[cmd.coreId];
        if (core === undefined) return;
        if (core.ownerId !== cmd.issuedBy) return;
        if (cmd.slotIndex < 0 || cmd.slotIndex >= 6) return;
        if (isSlotOccupied(this.state, core.id, cmd.slotIndex)) return;
        const def = BUILDINGS[cmd.building];
        const player = this.state.players[cmd.issuedBy];
        if (player.signal < def.cost) return;
        if (cmd.building === 'production_hub') {
          let hubsOwned = 0;
          for (const b of Object.values(this.state.buildings)) {
            if (b.ownerId === cmd.issuedBy && b.kind === 'production_hub') hubsOwned += 1;
          }
          if (hubsOwned >= MAX_HUBS_PER_PLAYER) return;
        }
        player.signal -= def.cost;
        const id = mintEntityId(this.state);
        const pos = slotWorldPos(core, cmd.slotIndex);
        const newBuilding: Building =
          cmd.building === 'production_hub'
            ? {
                id,
                ownerId: cmd.issuedBy,
                kind: 'production_hub',
                pos,
                hp: def.maxHp,
                maxHp: def.maxHp,
                buildProgress: 0,
                coreId: core.id,
                slotIndex: cmd.slotIndex,
                produces: { queue: [], progress: 0 },
                // V1.5+ permanent buildings are always online; the field is
                // present so renderer/sfx/bot reads can stay uniform across
                // building kinds. ForwardTurretNetworkSystem reasserts true.
                isOnline: true,
              }
            : {
                id,
                ownerId: cmd.issuedBy,
                kind: 'turret',
                pos,
                hp: def.maxHp,
                maxHp: def.maxHp,
                buildProgress: 0,
                coreId: core.id,
                slotIndex: cmd.slotIndex,
                attackCooldown: 0,
                target: null,
                facing: 0,
                isOnline: true,
              };
        this.state.buildings[id] = newBuilding;
        return;
      }
      case 'ability': {
        applyAbilityCommand(this.state, cmd);
        return;
      }
      case 'pick_doctrine': {
        // V1.5 PickDoctrineCommand validation:
        //   - reject if the pick window is closed.
        //   - reject if this player has already picked.
        // Both failures drop silently (Rule 12 — validation failures are not exceptions).
        if (!this.state.doctrinePickWindowOpen) return;
        const player = this.state.players[cmd.issuedBy];
        if (player.doctrineId !== null) return;
        player.doctrineId = cmd.doctrineId;
        // Telemetry: explicit pick → record the tick. Auto-assigned doctrines
        // (DoctrineSystem at window close) leave doctrinePickedAtTick=null,
        // which is the signal "this player ignored the pick window".
        this.state.stats.doctrinePickedAtTick[cmd.issuedBy] = this.state.tick;
        return;
      }
      default:
        unreachable(cmd, 'Unknown command kind');
    }
  }
}

function resolveProducer(
  state: MatchState,
  producerId: EntityId,
  issuedBy: PlayerId,
): ProductionSlot | null {
  const core = state.cores[producerId];
  if (core !== undefined) {
    if (core.ownerId !== issuedBy) return null;
    return core.produces;
  }
  const building = state.buildings[producerId];
  if (building === undefined) return null;
  if (building.ownerId !== issuedBy) return null;
  if (building.kind !== 'production_hub') return null;
  if (building.buildProgress < 1) return null;
  return building.produces;
}

function mintEntityId(state: MatchState): EntityId {
  const id = state.nextEntityId;
  state.nextEntityId = id + 1;
  return entityId(id);
}

function groupMoveAssignments(
  state: MatchState,
  grid: PathGrid,
  squadIds: readonly EntityId[],
  issuedBy: PlayerId,
  target: { x: number; y: number },
): Array<{ squad: Squad; target: { x: number; y: number } }> {
  const squads: Squad[] = [];
  const seen = new Set<EntityId>();
  for (const sid of squadIds) {
    if (seen.has(sid)) continue;
    seen.add(sid);
    const squad = state.squads[sid];
    if (squad === undefined) continue;
    if (squad.ownerId !== issuedBy) continue;
    squads.push(squad);
  }
  if (squads.length <= 1) {
    return squads.map((squad) => ({ squad, target: { x: target.x, y: target.y } }));
  }

  const centroid = squadCentroid(squads);
  const dx = target.x - centroid.x;
  const dy = target.y - centroid.y;
  const len = Math.hypot(dx, dy);
  const forward = len > 0.001
    ? { x: dx / len, y: dy / len }
    : facingVector(squads[0]!);
  const right = { x: -forward.y, y: forward.x };
  const offsets = formationOffsets(squads.length, forward, right);

  return squads.map((squad, index) => {
    const offset = offsets[index] ?? { x: 0, y: 0 };
    const desired = {
      x: target.x + offset.x,
      y: target.y + offset.y,
    };
    return {
      squad,
      target: nearestOpenFormationTarget(grid, desired, target),
    };
  });
}

function squadCentroid(squads: readonly Squad[]): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (const squad of squads) {
    x += squad.pos.x;
    y += squad.pos.y;
  }
  return { x: x / squads.length, y: y / squads.length };
}

function facingVector(squad: Squad): { x: number; y: number } {
  return { x: Math.cos(squad.facing), y: Math.sin(squad.facing) };
}

function formationOffsets(
  count: number,
  forward: { x: number; y: number },
  right: { x: number; y: number },
): Array<{ x: number; y: number }> {
  const columns = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / columns);
  const offsets: Array<{ x: number; y: number }> = [];
  for (let index = 0; index < count; index++) {
    const row = Math.floor(index / columns);
    const col = index % columns;
    const rowCount = row === rows - 1 ? count - row * columns : columns;
    const lateral = (col - (rowCount - 1) / 2) * GROUP_FORMATION_SPACING_PX;
    const depth = (row - (rows - 1) / 2) * GROUP_FORMATION_SPACING_PX;
    offsets.push({
      x: right.x * lateral - forward.x * depth,
      y: right.y * lateral - forward.y * depth,
    });
  }
  return offsets;
}

function nearestOpenFormationTarget(
  grid: PathGrid,
  desired: { x: number; y: number },
  fallback: { x: number; y: number },
): { x: number; y: number } {
  const clampedDesired = clampToWorld(grid, desired);
  const desiredCell = worldToCell(grid, clampedDesired.x, clampedDesired.y);
  if (!isBlocked(grid, desiredCell.cx, desiredCell.cy)) {
    return clampedDesired;
  }

  const fallbackCell = worldToCell(grid, fallback.x, fallback.y);
  for (let radius = 1; radius <= 4; radius++) {
    let best: { x: number; y: number } | null = null;
    let bestDistSq = Number.POSITIVE_INFINITY;
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== radius) continue;
        const cx = desiredCell.cx + dx;
        const cy = desiredCell.cy + dy;
        if (isBlocked(grid, cx, cy)) continue;
        const center = cellCenter(grid, cx, cy);
        const distSq = (center.x - clampedDesired.x) ** 2 + (center.y - clampedDesired.y) ** 2;
        if (distSq < bestDistSq) {
          best = center;
          bestDistSq = distSq;
        }
      }
    }
    if (best !== null) return best;
  }

  if (!isBlocked(grid, fallbackCell.cx, fallbackCell.cy)) {
    return clampToWorld(grid, fallback);
  }
  return clampedDesired;
}

function clampToWorld(grid: PathGrid, pos: { x: number; y: number }): { x: number; y: number } {
  return {
    x: clamp(pos.x, 1, grid.width - 1),
    y: clamp(pos.y, 1, grid.height - 1),
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function isPermanentBuildingKind(kind: string): kind is PermanentBuildingKind {
  return kind === 'production_hub' || kind === 'turret';
}
